import { useState } from 'react';
import { Link } from 'react-router-dom';
import ChatBoxIcon from './components/ChatBoxIcon';
import LiveChatModal from '../home/components/LiveChatModal';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: 'Account',
    question: 'How do I sign up?',
    answer: 'Click the "Sign Up" button at the top and enter your email, password, and name. You can start playing games immediately after registration.'
  },
  {
    category: 'Account',
    question: 'I forgot my password',
    answer: 'Click "Forgot Password" on the login page and a password reset link will be sent to your registered email address.'
  },
  {
    category: 'Account',
    question: 'How can I update my account information?',
    answer: 'After logging in, you can modify your personal information on the Profile page. Email changes may require verification.'
  },
  {
    category: 'Deposits & Withdrawals',
    question: 'How do I make a deposit?',
    answer: 'Go to the Wallet page and click the "Deposit" button to select your preferred payment method. We support credit cards, bank transfers, cryptocurrencies, and more.'
  },
  {
    category: 'Deposits & Withdrawals',
    question: 'How long does withdrawal take?',
    answer: 'Withdrawals are typically processed within 24 hours. Depending on the payment method, it may take 1-3 days to complete.'
  },
  {
    category: 'Deposits & Withdrawals',
    question: 'Is there a minimum deposit/withdrawal amount?',
    answer: 'The minimum deposit is $10 and the minimum withdrawal is $20. Maximum limits vary based on your VIP level.'
  },
  {
    category: 'Games',
    question: 'The game is not loading',
    answer: 'Try clearing your browser cache or using a different browser. If the problem persists, please contact customer support.'
  },
  {
    category: 'Games',
    question: 'How do I receive bonuses?',
    answer: 'Check the Promotions page for ongoing events and participate to receive bonuses. Some bonuses are automatically credited.'
  },
  {
    category: 'Games',
    question: 'What is the VIP program?',
    answer: 'The VIP program offers higher bonuses and special benefits as your level increases based on activity. Check the VIP page for more details.'
  },
  {
    category: 'Technical Support',
    question: 'Can I use it on mobile?',
    answer: 'Yes, it is optimized for all devices. You can use it directly from your mobile browser without installing any app.'
  },
  {
    category: 'Technical Support',
    question: 'Which browser should I use?',
    answer: 'We recommend the latest versions of Chrome, Safari, Firefox, or Edge. Keep your browser updated for the best experience.'
  },
  {
    category: 'Technical Support',
    question: 'Is it secure?',
    answer: 'We use SSL encryption and the latest security technology to protect your information. Regular security audits are conducted.'
  }
];

export default function SupportPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [isOpenLiveChatDialog, setIsOpenLiveChatDialog] = useState(false);
  const onOpenChatDlg = () => setIsOpenLiveChatDialog(true);
  const onCloseChatDlg = () => setIsOpenLiveChatDialog(false);

  const categories = ['All', 'Account', 'Deposits & Withdrawals', 'Games', 'Technical Support'];

  const filteredFAQs = selectedCategory === 'All' 
    ? faqData 
    : faqData.filter(faq => faq.category === selectedCategory);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all fields.');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        category: formData.category,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4sngr3rvmocq14jmpc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          category: 'General Inquiry',
          message: ''
        });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-xl border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-slot-machine-line text-[#FFD349] text-2xl"></i>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/50 group-hover:shadow-purple-500/70 transition-all duration-300">
                <i className="ri-customer-service-2-fill text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Customer Support</h1>
                <p className="text-xs text-purple-300">24/7 Support</p>
              </div>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-5-line text-purple-300"></i>
              <span className="text-sm font-medium text-purple-200">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full mb-6">
            <i className="ri-question-answer-line text-purple-300"></i>
            <span className="text-sm font-medium text-purple-200">How can we help you?</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Find answers quickly. If you can't find what you're looking for, use the contact form below.
          </p>
        </div>

        {/* FAQ Section */}
        <div className="mb-20">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800/70 border border-purple-500/20'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="max-w-4xl mx-auto space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-purple-500/20 overflow-hidden hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-slate-800/70 transition-colors duration-300"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-question-line text-purple-400 text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-medium rounded-full mb-2">
                        {faq.category}
                      </span>
                      <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                    </div>
                  </div>
                  <i className={`ri-arrow-down-s-line text-2xl text-slate-400 transition-transform duration-300 flex-shrink-0 ${
                    openFAQ === index ? 'rotate-180' : ''
                  }`}></i>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 pt-2">
                    <div className="pl-12 pr-4">
                      <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/20 border border-pink-500/30 rounded-full mb-6">
              <i className="ri-mail-send-line text-pink-300"></i>
              <span className="text-sm font-medium text-pink-200">Can't find an answer?</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-slate-300">
              We'll respond within 24 hours. The more details you provide, the faster we can help you.
            </p>
          </div>

          <form 
            id="support-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            style={{display: 'None'}}
            className="bg-slate-800/50 backdrop-blur-xl rounded-3xl shadow-xl border border-purple-500/20 p-8 sm:p-10"
          >
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-200 mb-2">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-purple-500/30 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 text-white placeholder-slate-500"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-200 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-purple-500/30 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 text-white placeholder-slate-500"
                  placeholder="example@email.com"
                />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-semibold text-slate-200 mb-2">
                  Inquiry Type <span className="text-red-400">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-purple-500/30 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 text-white cursor-pointer"
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Account">Account</option>
                  <option value="Deposits & Withdrawals">Deposits & Withdrawals</option>
                  <option value="Games">Games</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-200 mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-purple-500/30 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 resize-none text-white placeholder-slate-500"
                  placeholder="Please describe your inquiry in detail..."
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-400">Maximum 500 characters</p>
                  <p className="text-xs text-slate-400">{formData.message.length}/500</p>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill"></i>
                    Send Message
                  </span>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                  <p className="text-sm text-green-300 font-medium">
                    Your inquiry has been sent successfully! We'll respond as soon as possible.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-400 text-xl"></i>
                  <p className="text-sm text-red-300 font-medium">
                    An error occurred while sending. Please try again.
                  </p>
                </div>
              )}
            </div>
          </form>

          {/* Contact Info */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-purple-400 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-white mb-2">Email</h3>
              <p className="text-sm text-slate-300">support@digitwise.com</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-pink-400 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-white mb-2">Operating Hours</h3>
              <p className="text-sm text-slate-300">24/7 Available</p>
            </div>

            <ChatBoxIcon onOpenChatDlg={onOpenChatDlg}/>
          </div>
          <LiveChatModal isOpen={isOpenLiveChatDialog} onClose={onCloseChatDlg} />
        </div>
      </div>
    </div>
  );
}
